/**
 * AdminDirectorsPanel — Admin UI to tune every agent of the Directors
 * multi-agent pipeline (system prompts, model, sampling params, timeout)
 * and to run one-shot tests to audit each agent's raw output.
 *
 * Changes save globally (Supabase `admin_settings.key='directors_config'`)
 * and take effect for all users within ~20s (repo TTL).
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Save, RefreshCw, Play, Clapperboard, Camera, Package,
  Shirt, AudioLines, Scissors, Film, AlertTriangle, CheckCircle2, RotateCcw,
  Coins,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AGENTS = [
  { id: 'ad',        label: 'Asistente de Dirección', Icon: Clapperboard, phase: 'Fase 1 · Gatekeeper' },
  { id: 'dop',       label: 'Fotografía',             Icon: Camera,       phase: 'Fase 2 · Especialistas' },
  { id: 'arte',      label: 'Arte',                   Icon: Package,      phase: 'Fase 2 · Especialistas' },
  { id: 'vestuario', label: 'Vestuario',              Icon: Shirt,        phase: 'Fase 2 · Especialistas' },
  { id: 'sonido',    label: 'Sonido',                 Icon: AudioLines,   phase: 'Fase 2 · Especialistas' },
  { id: 'montaje',   label: 'Montaje',                Icon: Scissors,     phase: 'Fase 2 · Especialistas' },
  { id: 'director',  label: 'Director',               Icon: Film,         phase: 'Fase 3 · Síntesis' },
];

const AVAILABLE_MODELS = [
  { id: 'meta/meta-llama-3-70b-instruct', label: 'Llama-3 70B (Alta capacidad)' },
  { id: 'meta/meta-llama-3-8b-instruct',  label: 'Llama-3 8B (Rápido)' },
];

const AdminDirectorsPanel = () => {
  const [config, setConfig] = useState(null);
  const [initialConfig, setInitialConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeAgent, setActiveAgent] = useState('ad');

  // Test panel
  const [testGuion, setTestGuion] = useState('un perro corre por la playa al amanecer, atmósfera alegre, cámara acompaña por detrás');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/directors/config`);
      setConfig(res.data.config);
      setInitialConfig(JSON.parse(JSON.stringify(res.data.config)));
      if (res.data.config?.meta?.table_missing) {
        toast.warning('Tabla admin_settings no existe. Corré el SQL indicado en el docstring del repo.');
      }
    } catch (e) {
      toast.error(`No se pudo cargar la config: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const dirty = useMemo(() => {
    if (!config || !initialConfig) return false;
    return JSON.stringify(config.agents) !== JSON.stringify(initialConfig.agents)
        || JSON.stringify(config.pipeline) !== JSON.stringify(initialConfig.pipeline)
        || JSON.stringify(config.credits) !== JSON.stringify(initialConfig.credits);
  }, [config, initialConfig]);

  const updateAgent = (agentId, patch) => {
    setConfig((c) => ({
      ...c,
      agents: { ...c.agents, [agentId]: { ...c.agents[agentId], ...patch } },
    }));
  };

  const updateCredits = (patch) => {
    setConfig((c) => ({
      ...c,
      credits: { ...(c.credits || { ad: 3, specialist: 1, director: 4 }), ...patch },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/admin/directors/config`, {
        agents: config.agents,
        pipeline: config.pipeline,
        credits: config.credits,
      });
      setConfig(res.data.config);
      setInitialConfig(JSON.parse(JSON.stringify(res.data.config)));
      toast.success('Config guardada. Se aplica en <20s a todas las sesiones.');
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail?.code === 'admin_settings_table_missing') {
        // Surface missing-table banner + copy SQL so admin can act in one click.
        setConfig((c) => ({
          ...c,
          meta: { ...(c?.meta || {}), table_missing: true, missing_table_sql: detail.sql },
        }));
        toast.error('Falta crear la tabla admin_settings en Supabase — copiá el SQL de arriba.', { duration: 6000 });
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.message || e.message);
        toast.error(`Falló el guardado: ${msg}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    if (!testGuion.trim()) return toast.error('Escribí un guion de prueba');
    setTesting(true); setTestResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/admin/directors/test`, {
        agent: activeAgent,
        guion: testGuion,
        node_type: 'generic',
        specialists_payload: activeAgent === 'director'
          ? { dop: { esquema_luz: 'contraluz cálido', tipo_plano: 'general' }, arte: {}, vestuario: {}, sonido: {}, montaje: {} }
          : null,
      });
      setTestResult(res.data);
    } catch (e) {
      setTestResult({ success: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setTesting(false);
    }
  };

  const revert = () => {
    setConfig(JSON.parse(JSON.stringify(initialConfig)));
    toast.info('Cambios revertidos.');
  };

  if (loading || !config) {
    return (
      <div className="admin-directors-loading">
        <Loader2 size={22} className="spin" /> Cargando config…
      </div>
    );
  }

  const agent = config.agents[activeAgent];
  const agentMeta = AGENTS.find((a) => a.id === activeAgent);

  return (
    <div className="admin-directors" data-testid="admin-directors-panel">
      {config.meta?.table_missing && (
        <div className="admin-directors-warn" data-testid="admin-directors-missing-table">
          <div className="admin-directors-warn-head">
            <AlertTriangle size={14} />
            <span>
              Tabla <code>admin_settings</code> no existe en Supabase. Los cambios NO se van a persistir hasta que corras el SQL de creación.
            </span>
          </div>
          {config.meta.missing_table_sql && (
            <>
              <pre className="admin-directors-warn-sql">{config.meta.missing_table_sql}</pre>
              <button
                type="button"
                className="admin-directors-warn-copy"
                data-testid="admin-directors-copy-sql"
                onClick={() => {
                  navigator.clipboard.writeText(config.meta.missing_table_sql);
                  toast.success('SQL copiado. Pegalo en el editor de Supabase.');
                }}
              >
                Copiar SQL
              </button>
            </>
          )}
        </div>
      )}

      <div className="admin-directors-toolbar">
        <div className="admin-directors-meta">
          {config.meta?.updated_at && (
            <span>Última edición: <b>{new Date(config.meta.updated_at).toLocaleString()}</b> por {config.meta.updated_by || 'desconocido'}</span>
          )}
        </div>
        <div className="admin-directors-actions">
          <button className="btn ghost" onClick={load} disabled={loading}>
            <RefreshCw size={13} /> Recargar
          </button>
          <button className="btn ghost" onClick={revert} disabled={!dirty || saving}>
            <RotateCcw size={13} /> Descartar
          </button>
          <button
            className="btn primary"
            onClick={save}
            disabled={!dirty || saving}
            data-testid="admin-directors-save"
          >
            {saving ? <><Loader2 size={13} className="spin" /> Guardando…</> : <><Save size={13} /> Guardar</>}
          </button>
        </div>
      </div>

      {/* Credits pricing — global, applies to every user */}
      <section className="admin-directors-credits" data-testid="admin-directors-credits">
        <header>
          <Coins size={15} />
          <h4>Costos en créditos por ejecución</h4>
          <span className="admin-directors-credits-total" data-testid="admin-directors-credits-total">
            Total: <b>{
              (config.credits?.ad ?? 3)
              + 5 * (config.credits?.specialist ?? 1)
              + (config.credits?.director ?? 4)
            }</b> créditos
          </span>
        </header>
        <div className="admin-directors-credits-grid">
          <label>
            <span>AD (Gatekeeper Llama-3 70B)</span>
            <input type="number" min={0} max={100} step={1}
              value={config.credits?.ad ?? 3}
              onChange={(e) => updateCredits({ ad: Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)) })}
              data-testid="admin-directors-credits-ad"
            />
          </label>
          <label>
            <span>Especialista × 5 (Llama-3 8B)</span>
            <input type="number" min={0} max={100} step={1}
              value={config.credits?.specialist ?? 1}
              onChange={(e) => updateCredits({ specialist: Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)) })}
              data-testid="admin-directors-credits-specialist"
            />
            <small>× 5 = {5 * (config.credits?.specialist ?? 1)}</small>
          </label>
          <label>
            <span>Director (Síntesis Llama-3 70B)</span>
            <input type="number" min={0} max={100} step={1}
              value={config.credits?.director ?? 4}
              onChange={(e) => updateCredits({ director: Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)) })}
              data-testid="admin-directors-credits-director"
            />
          </label>
        </div>
        <p className="admin-directors-credits-hint">
          Se descuentan solo si el pipeline termina con éxito. El cambio impacta a todos los usuarios en menos de 20s.
        </p>
      </section>

      <div className="admin-directors-body">
        {/* Agent selector */}
        <aside className="admin-directors-nav">
          {AGENTS.map((a) => (
            <button
              key={a.id}
              className={`admin-directors-nav-item ${activeAgent === a.id ? 'active' : ''}`}
              onClick={() => setActiveAgent(a.id)}
              data-testid={`admin-directors-nav-${a.id}`}
            >
              <a.Icon size={14} />
              <div>
                <b>{a.label}</b>
                <span>{a.phase}</span>
              </div>
            </button>
          ))}
        </aside>

        {/* Editor */}
        <section className="admin-directors-editor">
          <div className="admin-directors-editor-head">
            <agentMeta.Icon size={16} />
            <h3>{agentMeta.label}</h3>
            <span className="phase-badge">{agentMeta.phase}</span>
          </div>

          <div className="admin-directors-grid">
            <label>
              <span>Modelo LLM</span>
              <select
                value={agent.model}
                onChange={(e) => updateAgent(activeAgent, { model: e.target.value })}
                data-testid={`admin-directors-model-${activeAgent}`}
              >
                {AVAILABLE_MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </label>
            <label>
              <span>Timeout (ms)</span>
              <input type="number" min={5000} max={600000} step={1000}
                value={agent.timeout_ms}
                onChange={(e) => updateAgent(activeAgent, { timeout_ms: parseInt(e.target.value, 10) || 60000 })}
                data-testid={`admin-directors-timeout-${activeAgent}`}
              />
            </label>
            <label>
              <span>Max tokens</span>
              <input type="number" min={64} max={4096} step={16}
                value={agent.max_tokens}
                onChange={(e) => updateAgent(activeAgent, { max_tokens: parseInt(e.target.value, 10) || 350 })}
              />
            </label>
            <label>
              <span>Temperatura</span>
              <input type="number" min={0} max={2} step={0.05}
                value={agent.temperature}
                onChange={(e) => updateAgent(activeAgent, { temperature: parseFloat(e.target.value) || 0 })}
              />
            </label>
            <label>
              <span>Top-p</span>
              <input type="number" min={0} max={1} step={0.05}
                value={agent.top_p}
                onChange={(e) => updateAgent(activeAgent, { top_p: parseFloat(e.target.value) || 0 })}
              />
            </label>
            <label>
              <span>Presence penalty</span>
              <input type="number" min={-2} max={2} step={0.05}
                value={agent.presence_penalty}
                onChange={(e) => updateAgent(activeAgent, { presence_penalty: parseFloat(e.target.value) || 0 })}
              />
            </label>
          </div>

          <label className="admin-directors-prompt-field">
            <span>System prompt</span>
            <textarea
              rows={18}
              value={agent.system_prompt}
              onChange={(e) => updateAgent(activeAgent, { system_prompt: e.target.value })}
              spellCheck={false}
              data-testid={`admin-directors-prompt-${activeAgent}`}
            />
          </label>
        </section>

        {/* Test panel */}
        <aside className="admin-directors-test">
          <h4>Probar este agente</h4>
          <label>
            <span>Guion de prueba</span>
            <textarea
              rows={4}
              value={testGuion}
              onChange={(e) => setTestGuion(e.target.value)}
              data-testid="admin-directors-test-guion"
            />
          </label>
          <button
            className="btn primary full"
            onClick={runTest}
            disabled={testing}
            data-testid="admin-directors-test-run"
          >
            {testing ? <><Loader2 size={13} className="spin" /> Ejecutando…</> : <><Play size={13} /> Ejecutar {agentMeta.label}</>}
          </button>

          {testResult && (
            <div className={`admin-directors-test-result ${testResult.success ? 'ok' : 'err'}`} data-testid="admin-directors-test-result">
              <div className="admin-directors-test-head">
                {testResult.success
                  ? <><CheckCircle2 size={13} /> OK</>
                  : <><AlertTriangle size={13} /> Falló</>}
                <span>{testResult.elapsed_ms ? `${testResult.elapsed_ms}ms` : ''}</span>
              </div>
              <pre>{testResult.success
                ? JSON.stringify(testResult.result, null, 2)
                : testResult.error}</pre>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default AdminDirectorsPanel;
